import type { ActionCreator, Actions, State, ThunkCreator } from 'easy-peasy';
import type { StoreModel } from '@app/store/types/store-model';
import type { TJob, TJobMainClient } from '@model/Job/Job.type';
import type { TJobProgressSegment } from '@components/navigation/JobProgressSwitch/JobProgressSwitch.types';
import type {
  TJobClient,
  TJobClientsDeletePayload,
  TJobClientsGetPayload,
  TJobClientsPostPayload,
  TJobClientsPutPayload,
} from '@model/Job/clients/Clients.type';
import { CONTRACT_JOB } from '@model/Job/_constants/Job.constants';
import { CONTRACT_SERVICE_TYPES } from '@model/Contract/Contract.constants';
import type { TDownloadResponseData } from '@app/_types/downloads.types';
import { PAY_TRANSPARENCY_RULES_COUNTRIES } from '@app/_constants/locations.constants';

export const jobStoreSelector = (state: State<StoreModel>): TJob | null => {
  if (state.job.data) {
    return state.job.data;
  }

  const contract = state.contract.data;

  if (!contract) {
    return null;
  }

  return {
    ...CONTRACT_JOB,
    client: contract.client,
    companyName: contract.companyName,
    contractId: contract.id,
    id: contract.id,
    recruitmentConsultant: contract.salesConsultant,
    salesConsultant: contract.salesConsultant,
    reverseGroupCompany: contract.reverseGroupCompany,
    serviceType: contract.serviceType,
    title: contract.title,
  };
};

export const jobIdSelector = (state: State<StoreModel>): string | null => {
  if (state.job.currentJobId) {
    return state.job.currentJobId;
  }

  return state.contract.data?.id ?? null;
};

export const jobActualStoreSelector = (state: State<StoreModel>): TJob | null =>
  state.job.data;

export const jobLoadingSelector = (state: State<StoreModel>): boolean =>
  state.job.loading;

export const jobProgressSelector = (
  state: State<StoreModel>,
): TJobProgressSegment | null => state.job.progress;

// Action Getters
export const setCurrentJobIdSelector = (
  state: Actions<StoreModel>,
): ActionCreator<string> => state.job.setCurrentJobId;

export const setJobProgressSelector = (
  state: Actions<StoreModel>,
): ActionCreator<TJobProgressSegment> => state.job.setJobProgress;

export const jobClientSelector = (
  state: State<StoreModel>,
): TJobMainClient | null => {
  if (state.job.data?.client) {
    return state.job.data?.client;
  }

  return state.contract.data?.client ?? null;
};

export const jobCompanyNameSelector = (
  state: State<StoreModel>,
): string | null => state.job.data?.companyName ?? null;

export const selectorJobIsReverseMarketing = (
  state: State<StoreModel>,
): boolean =>
  state.job.data?.serviceType === CONTRACT_SERVICE_TYPES.REVERSE_MARKETING;

export const selectorJobProfilingBoardUrl = (
  state: State<StoreModel>,
): string | null => state.job.data?.profilingBoardUrl ?? null;

export const selectorJobHasProfilingMeetingDocument = (
  state: State<StoreModel>,
): boolean => state.job.data?.hasProfilingMeetingDocument ?? false;

export const selectorJobArePayTransparencyRulesApplied = (
  state: State<StoreModel>,
): boolean => {
  const workplaceCountry = state.job.data?.workplace.country;

  if (!workplaceCountry) {
    return false;
  }

  return PAY_TRANSPARENCY_RULES_COUNTRIES.includes(workplaceCountry);
};

// Thunk actions
export const getJobSelector = (
  state: Actions<StoreModel>,
): ThunkCreator<{ jobId: string }, Promise<TJob | undefined>> =>
  state.job.getJob;

export const jobClientsSelector = (state: State<StoreModel>): TJobClient[] =>
  state.job.clients.clients;

export const getJobClientsSelector = (
  state: Actions<StoreModel>,
): ThunkCreator<TJobClientsGetPayload, TJobClient[]> =>
  state.job.clients.getClients;

export const postJobClientSelector = (
  state: Actions<StoreModel>,
): ThunkCreator<TJobClientsPostPayload, TJobClient> =>
  state.job.clients.postClients;

export const putJobClientSelector = (
  state: Actions<StoreModel>,
): ThunkCreator<TJobClientsPutPayload, TJobClient> =>
  state.job.clients.putClients;

export const deleteJobClientSelector = (
  state: Actions<StoreModel>,
): ThunkCreator<TJobClientsDeletePayload, void> =>
  state.job.clients.deleteClients;

export const selectorGetJobProfilingMeetingDocument = (
  state: Actions<StoreModel>,
): ThunkCreator<{ jobId: string }, Promise<TDownloadResponseData | null>> =>
  state.job.getJobProfilingMeetingDocument;
